import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getPermit, deletePermit } from "../../api/permit";
import FileList from "../../components/common/papers/FileList";
import { formatDate } from "../../utils/date";

export default function PermitViewPage() {
  const { id, permitId } = useParams();
  const navigate = useNavigate();

  const [permit, setPermit] = useState(null);
  const [loading, setLoading] = useState(true);

  const STATUS_LABELS = {
    planned: "계획",
    in_progress: "진행중",
    completed: "완료",
    delayed: "지연",
  };

  useEffect(() => {
    getPermit(permitId).then((data) => {
      setPermit(data);
      setLoading(false);
    });
  }, [permitId]);

  const handleDelete = async () => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    await deletePermit(permitId);
    alert("삭제되었습니다.");
    navigate(`/project/${id}/permit/list`);
  };

  if (loading) {
    return (
      <div className="mobile-page-root mb-16">
        <div className="mobile-page-root-inner py-8">
          <div className="bg-white rounded-lg p-6 border text-center">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">인허가 정보 로딩 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!permit) {
    return (
      <div className="mobile-page-root mb-16">
        <div className="mobile-page-root-inner py-8">
          <div className="bg-white rounded-lg p-6 border text-center">
            <p className="text-sm text-gray-500">데이터가 없습니다.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-3 border min-w-0">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-gray-900">인허가 상세</h2>
              <p className="text-xs text-gray-500 mt-0.5">{permit.name}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/project/${id}/permit/edit/${permitId}`}
              className="btn-edit"
            >
              수정
            </Link>
            <button onClick={handleDelete} className="btn-delete">
              삭제
            </button>
            <Link to={`/project/${id}/permit/list`} className="btn-basic">
              목록
            </Link>
          </div>
        </div>

        {/* 기본 정보 */}
        <div className="bg-white rounded-lg border min-w-0">
          <div className="bg-gray-50 px-3 py-2 border-b">
            <div className="text-sm font-medium text-gray-900">기본 정보</div>
          </div>
          <div className="p-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-500 mb-1">인허가명</div>
                <div className="text-sm text-gray-900 font-medium break-words">
                  {permit.name || "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">종류</div>
                <div className="text-sm text-gray-900 break-words">
                  {permit.permit_type || "-"}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-500 mb-1">발급기관</div>
                <div className="text-sm text-gray-900 break-words">
                  {permit.authority || "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">상태</div>
                <span
                  className={`inline-block px-2 py-1 rounded text-xs font-medium ${permit.status === "completed"
                    ? "bg-green-100 text-green-700"
                    : permit.status === "in_progress"
                      ? "bg-blue-100 text-blue-700"
                      : permit.status === "delayed"
                        ? "bg-red-100 text-red-700"
                        : "bg-gray-100 text-gray-700"
                    }`}
                >
                  {STATUS_LABELS[permit.status] || "-"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 날짜 정보 */}
        <div className="bg-white rounded-lg border min-w-0">
          <div className="bg-gray-50 px-3 py-2 border-b">
            <div className="text-sm font-medium text-gray-900">날짜 정보</div>
          </div>
          <div className="p-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-500 mb-1">예정 접수일</div>
                <div className="text-sm text-gray-900">
                  {permit.planned_submit_date
                    ? formatDate(permit.planned_submit_date)
                    : "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">실제 접수일</div>
                <div className="text-sm text-gray-900">
                  {permit.submit_date ? formatDate(permit.submit_date) : "-"}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-500 mb-1">예정 완료일</div>
                <div className="text-sm text-gray-900">
                  {permit.planned_date ? formatDate(permit.planned_date) : "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">실제 완료일</div>
                <div className="text-sm text-gray-900">
                  {permit.actual_date ? formatDate(permit.actual_date) : "-"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 첨부파일 */}
        <div className="bg-white rounded-lg border p-3 min-w-0">
          <FileList dns={permit.doc_dns} />
        </div>

        {/* 비고 */}
        {permit.description && (
          <div className="bg-white rounded-lg border min-w-0">
            <div className="bg-gray-50 px-3 py-2 border-b">
              <div className="text-sm font-medium text-gray-900">비고</div>
            </div>
            <div className="p-3">
              <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed break-words">
                {permit.description}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
